/**
 * Background Calls Atom
 *
 * Manages test calls that are running in the background (when user closes modal).
 * Used for Phase 1 notifications: persistent toast for phone calls.
 */

import { atom } from "jotai";
import type { TestCallStatus } from "@/isomorphic/schemas/agent-test-call";

// ============================================================================
// TYPES
// ============================================================================

export interface BackgroundCall {
  testCallId: string;
  agentId: string;
  agentName: string;
  phaseId: string;
  phaseName: string;
  startedAt: Date;
  callStatus: TestCallStatus | null;
}

// ============================================================================
// ATOMS
// ============================================================================

const _backgroundCallsAtom = atom<BackgroundCall[]>([]);

export const backgroundCallsAtom = atom((get) => get(_backgroundCallsAtom));

export const hasBackgroundCallsAtom = atom((get) => get(_backgroundCallsAtom).length > 0);

export const backgroundCallByIdAtom = atom((get) => {
  return (testCallId: string) => get(_backgroundCallsAtom).find((call) => call.testCallId === testCallId);
});

// ============================================================================
// ACTIONS
// ============================================================================

export const addBackgroundCallAtom = atom(null, (get, set, call: BackgroundCall) => {
  const calls = get(_backgroundCallsAtom);
  const exists = calls.some((c) => c.testCallId === call.testCallId);
  if (!exists) {
    set(_backgroundCallsAtom, [...calls, call]);
  }
});

export const updateBackgroundCallStatusAtom = atom(
  null,
  (get, set, { testCallId, callStatus }: { testCallId: string; callStatus: TestCallStatus | null }) => {
    const calls = get(_backgroundCallsAtom);
    set(
      _backgroundCallsAtom,
      calls.map((call) => (call.testCallId === testCallId ? { ...call, callStatus } : call)),
    );
  },
);

export const removeBackgroundCallAtom = atom(null, (get, set, testCallId: string) => {
  const calls = get(_backgroundCallsAtom);
  set(
    _backgroundCallsAtom,
    calls.filter((call) => call.testCallId !== testCallId),
  );
});

export const clearBackgroundCallsAtom = atom(null, (_get, set) => {
  set(_backgroundCallsAtom, []);
});
